import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  Box, Typography, TextField, Button,
  Dialog, DialogTitle, DialogContent, DialogActions,
  Chip, IconButton,
  FormControl, InputLabel, Select, MenuItem, FormControlLabel, Checkbox,
  Snackbar, Alert, Backdrop, CircularProgress,
  Divider, Tooltip, Paper, InputAdornment, useTheme, useMediaQuery,
  Autocomplete, Menu, ListItemIcon, ListItemText,
  Accordion, AccordionSummary, AccordionDetails,
} from '@mui/material';
import { Add, Cancel, Save, Edit, Delete, NetworkCheck, ArrowDropDown, ExpandMore, Storage } from '@mui/icons-material';
import AssessmentOutlined from '@mui/icons-material/AssessmentOutlined';
import CheckCircle from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import { useNavigate } from 'react-router-dom';

const LOGO_BASE = '/static/assets';

const ModuleLogo: React.FC<{ src: string; alt: string; size?: number }> = ({ src, alt, size = 48 }) => (
  <img src={`${LOGO_BASE}/${src}`} alt={alt} style={{ width: size, height: size, objectFit: 'contain' }} />
);
import { DynamicBreadcrumbs } from 'components/Breadcrumb';
import apiClient from 'utils/ApiClient';
import { useModulePermissions } from 'hooks/useModulePermissions';
import {
  SWIPE_ACTION_WIDTH_PX,
  SWIPE_DIRECTION_LOCK_PX,
  clampSwipeOffset,
  computeSnappedOffset,
} from 'utils/iosSwipeRow';
import { LokiSettingsLabelBrowser } from './LokiSettingsLabelBrowser';
import { TenantsAccordion, IlTenant } from './TenantsAccordion';
import {
  fetchDefaultReportsClickHouseConfigId,
  setDefaultReportsClickHouseConfigId,
} from '../reportsDefaultServer';
import { REPORTS_QUICK_RANGES } from '../Reports/reportsTimeRange';

interface HttpHeader {
  key: string;
  value: string;
}

interface CompanyMemberOption {
  _id: string;
  email: string;
  label: string;
}

interface ModuleConfiguration {
  _id?: string;
  moduleType: string;
  name: string;
  url: string;
  authMethod: string;
  basicAuthUser: string;
  basicAuthPassword: string;
  tlsSelfSignedCert: boolean;
  tlsClientAuth: boolean;
  tlsSkipValidation: boolean;
  httpHeaders: HttpHeader[];
  requestTimeout: number;
  allowedCookies: string;
  /** Loki: label name -> selected values (stored with this server config) */
  labelValueSelections?: Record<string, string[]>;
  /** ClickHouse: tenant identifier for analytics site validation */
  tenantId?: string;
  /** ClickHouse: site identifier for analytics site validation */
  siteId?: string;
  /** ClickHouse: database name that backs this tenant/site's website_events table */
  dbName?: string;
  /**
   * ClickHouse: true when the URL fronts a replicated cluster (typically a load
   * balancer spreading requests across replicas). The server then provisions
   * ReplicatedMergeTree tables with ON CLUSTER DDL so a write that lands on any
   * replica is readable from all of them. False = single-node MergeTree.
   */
  clusterEnabled?: boolean;
  /** ClickHouse: cluster name from system.clusters; required when clusterEnabled */
  clusterName?: string;
  /**
   * ClickHouse: allow the Infralytiqs SDK on this tenant/site to capture the
   * precise visitor location (browser Geolocation API, visitor-consented).
   * Delivered to the SDK via the public client-config endpoint.
   */
  preciseVisitorLocation?: boolean;
  /**
   * ClickHouse: default Reports time-range (quick-range label, e.g. "Last 24 hours")
   * applied on the Reports page when the browser has no stored time-range history
   * for this server + suite. Empty = use the built-in default.
   */
  defaultTimeRange?: string;
  /** SYS-User ids (same company) assigned to this config */
  assignedUserIds?: string[];
  /**
   * Report suites configured under this connection. Present on configs that
   * support reporting (today: ClickHouse). The Copy-from-existing flow reads
   * this and forwards a sanitized version on the create payload so the new
   * config inherits the suites + reports tree.
   */
  reportSuites?: Array<{ name?: string; reports?: any[]; [k: string]: any }>;
  createdAt?: string;
  updatedAt?: string;
}

const MODULE_TYPES = [
  { key: 'Loki', label: 'Loki Server', logo: 'loki_logo.svg', available: true },
  { key: 'ClickHouse', label: 'ClickHouse Server', logo: 'clickhouse_logo.svg', available: true },
  { key: 'InfluxDB', label: 'Influx DB', logo: 'influxdb_logo.svg', available: false },
  { key: 'MongoDB', label: 'MongoDB', logo: 'mongodb_logo.svg', available: false },
];

const MODULE_LOGO_MAP: Record<string, string> = {
  Loki: 'loki_logo.svg',
  ClickHouse: 'clickhouse_logo.svg',
  InfluxDB: 'influxdb_logo.svg',
  MongoDB: 'mongodb_logo.svg',
};

function settingsConfigTileId(config: ModuleConfiguration): string {
  return config._id || `${config.moduleType}:${config.name}`;
}

const defaultLokiForm: Omit<ModuleConfiguration, '_id' | 'createdAt' | 'updatedAt'> = {
  moduleType: 'Loki',
  name: '',
  url: '',
  authMethod: 'none',
  basicAuthUser: '',
  basicAuthPassword: '',
  tlsSelfSignedCert: false,
  tlsClientAuth: false,
  tlsSkipValidation: false,
  httpHeaders: [],
  requestTimeout: 30,
  allowedCookies: '',
  labelValueSelections: {},
  tenantId: '',
  assignedUserIds: [],
};

const defaultClickHouseForm: Omit<ModuleConfiguration, '_id' | 'createdAt' | 'updatedAt'> = {
  moduleType: 'ClickHouse',
  name: '',
  url: '',
  authMethod: 'none',
  basicAuthUser: '',
  basicAuthPassword: '',
  tlsSelfSignedCert: false,
  tlsClientAuth: false,
  tlsSkipValidation: false,
  httpHeaders: [],
  requestTimeout: 30,
  allowedCookies: '',
  tenantId: '',
  siteId: '',
  dbName: '',
  clusterEnabled: false,
  clusterName: '',
  preciseVisitorLocation: false,
  defaultTimeRange: '',
  assignedUserIds: [],
};

const MODULE_DEFAULT_FORMS: Record<string, Omit<ModuleConfiguration, '_id' | 'createdAt' | 'updatedAt'>> = {
  Loki: defaultLokiForm,
  ClickHouse: defaultClickHouseForm,
};

/** Matches server-side test payload so Save stays disabled after connection-affecting edits until re-test. */
function computeLokiConnectionFingerprint(data: {
  url: string;
  authMethod: string;
  basicAuthUser: string;
  basicAuthPassword: string;
  tlsSelfSignedCert: boolean;
  tlsClientAuth: boolean;
  tlsSkipValidation: boolean;
  httpHeaders: HttpHeader[];
  requestTimeout: number;
  clusterEnabled?: boolean;
  clusterName?: string;
}): string {
  const normalizedHeaders = [...data.httpHeaders]
    .filter(h => h.key?.trim() && h.value !== undefined)
    .map(h => ({ key: h.key.trim(), value: String(h.value).trim() }))
    .sort((a, b) => a.key.localeCompare(b.key));
  return JSON.stringify({
    url: data.url.trim(),
    authMethod: data.authMethod,
    basicAuthUser: data.basicAuthUser,
    basicAuthPassword: data.basicAuthPassword,
    tlsSelfSignedCert: data.tlsSelfSignedCert,
    tlsClientAuth: data.tlsClientAuth,
    tlsSkipValidation: data.tlsSkipValidation,
    httpHeaders: normalizedHeaders,
    requestTimeout: data.requestTimeout,
    // Cluster settings are part of the tested surface: Test connection verifies
    // the named cluster exists, so changing them must force a re-test. Loki
    // forms leave these undefined, keeping their fingerprint unchanged.
    clusterEnabled: data.clusterEnabled,
    clusterName: data.clusterEnabled ? (data.clusterName || '').trim() : undefined,
  });
}

export const Settings: React.FC = () => {
  const theme = useTheme();
  /** Match Data Sets tile hero background (PROFESSIONAL light/dark + DEFAULT fallbacks). */
  const configTileGradient = theme.palette.dashboard?.counterHeroGradient
    ?? `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 60%, ${theme.palette.secondary.main} 100%)`;
  /** PROFESSIONAL light: dark module badge on hero tiles (same as Data Sets labels). */
  const configTileModuleBadgeColor = theme.palette.dashboard?.heroTileForegroundColor
    ? 'rgb(25, 25, 25)'
    : '#fff';
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [settingsTileSwipeRow, setSettingsTileSwipeRow] = useState<{
    tileId: string;
    offset: number;
    dragging: boolean;
  } | null>(null);
  const settingsSwipeSessionRef = useRef<{
    tileId: string;
    startX: number;
    startY: number;
    baseOffset: number;
    lock: 'h' | 'v' | null;
  } | null>(null);
  const settingsLatestSwipeOffsetRef = useRef(0);
  const { hasInfralytiqsAccess, isUserSuperAdmin } = useModulePermissions();
  const canAccessInfralytiqsSettings = hasInfralytiqsAccess('admin') || isUserSuperAdmin;
  const showCompanyUserAssignPicker = hasInfralytiqsAccess('admin') || isUserSuperAdmin;
  const [configurations, setConfigurations] = useState<ModuleConfiguration[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [newConfigMenuAnchor, setNewConfigMenuAnchor] = useState<null | HTMLElement>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState<'add' | 'view' | 'edit'>('add');
  const [editingConfigId, setEditingConfigId] = useState<string | null>(null);
  const [selectedModule, setSelectedModule] = useState<string | null>(null);
  const [formData, setFormData] = useState(defaultLokiForm);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  const [lokiTestLoading, setLokiTestLoading] = useState(false);
  const [lokiTestMessage, setLokiTestMessage] = useState('');
  const [lokiTestSeverity, setLokiTestSeverity] = useState<'success' | 'error' | 'info' | null>(null);
  const [verifiedLokiFingerprint, setVerifiedLokiFingerprint] = useState<string | null>(null);
  const [lokiLabelBrowserGeneration, setLokiLabelBrowserGeneration] = useState(0);
  const [companyMembers, setCompanyMembers] = useState<CompanyMemberOption[]>([]);

  /**
   * "Copy from existing" state used by the Add-config dialog. `copyFromConfigId`
   * is the source config's _id (or null when the user has not chosen to copy);
   * `copiedReportSuites` holds the deep-copy payload that will be forwarded
   * to `POST /il/settings/module-config` when saving a ClickHouse config.
   */
  const [copyFromConfigId, setCopyFromConfigId] = useState<string | null>(null);
  const [copiedReportSuites, setCopiedReportSuites] = useState<NonNullable<ModuleConfiguration['reportSuites']>>([]);
  const [defaultReportsClickHouseConfigId, setDefaultReportsClickHouseConfigIdState] = useState<string | null>(null);
  const [ilTenants, setIlTenants] = useState<IlTenant[]>([]);

  const resetLokiConnectionUi = useCallback(() => {
    setLokiTestLoading(false);
    setLokiTestMessage('');
    setLokiTestSeverity(null);
    setVerifiedLokiFingerprint(null);
    setLokiLabelBrowserGeneration(0);
  }, []);

  const resetCopyFromState = useCallback(() => {
    setCopyFromConfigId(null);
    setCopiedReportSuites([]);
  }, []);

  const fetchIlTenants = useCallback(async () => {
    try {
      const response = await apiClient.get('/il/settings/tenants');
      setIlTenants(Array.isArray(response.data) ? response.data : []);
    } catch {
      setIlTenants([]);
    }
  }, []);

  const fetchConfigurations = useCallback(async () => {
    try {
      setLoading(true);
      const [response, defaultConfigId] = await Promise.all([
        apiClient.get('/il/settings/module-configs'),
        fetchDefaultReportsClickHouseConfigId(),
      ]);
      setConfigurations(response.data || []);
      setDefaultReportsClickHouseConfigIdState(defaultConfigId);
    } catch (error: any) {
      showMessage(error.message || 'Failed to load configurations', 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!canAccessInfralytiqsSettings) return;
    fetchConfigurations();
    fetchIlTenants();
  }, [fetchConfigurations, fetchIlTenants, canAccessInfralytiqsSettings]);

  useEffect(() => {
    if (!dialogOpen || !canAccessInfralytiqsSettings) return;
    fetchIlTenants();
  }, [dialogOpen, canAccessInfralytiqsSettings, fetchIlTenants]);

  useEffect(() => {
    if (!dialogOpen || !showCompanyUserAssignPicker) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await apiClient.get('/il/settings/company-members');
        if (!cancelled) {
          setCompanyMembers(Array.isArray(res.data) ? res.data : []);
        }
      } catch {
        if (!cancelled) setCompanyMembers([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [dialogOpen, showCompanyUserAssignPicker]);

  const showMessage = (message: string, severity: 'success' | 'error') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      showMessage('Server name is required', 'error');
      return;
    }
    if (!formData.url.trim()) {
      showMessage('URL is required', 'error');
      return;
    }
    if ((selectedModule === 'Loki' || selectedModule === 'ClickHouse') && !lokiConnectionVerified) {
      showMessage('Run Test connection successfully before saving.', 'error');
      return;
    }

    try {
      setLoading(true);
      if (dialogMode === 'edit' && editingConfigId) {
        await apiClient.patch(`/il/settings/module-config/${editingConfigId}`, formData);
        showMessage('Module configuration updated successfully', 'success');
      } else {
        // Deep-copy: when adding a ClickHouse config off the back of a
        // "Copy from existing" selection, forward the source's report suites
        // tree so the server clones it under the new config. For Loki (or
        // when no copy was chosen) the field is omitted entirely.
        const payload: Record<string, any> = { ...formData };
        if (
          selectedModule === 'ClickHouse'
          && copyFromConfigId
          && copiedReportSuites.length > 0
        ) {
          payload.reportSuites = copiedReportSuites;
        }
        await apiClient.post('/il/settings/module-config', payload);
        const suffix = payload.reportSuites
          ? ` (deep-copied ${copiedReportSuites.length} suite(s) · ${copiedReportCount} report(s))`
          : '';
        showMessage(`Module configuration added successfully${suffix}`, 'success');
      }
      setDialogOpen(false);
      resetCopyFromState();
      fetchConfigurations();
    } catch (error: any) {
      showMessage(error.message || 'Failed to add configuration', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (configId: string) => {
    try {
      setLoading(true);
      await apiClient.delete(`/il/settings/module-config/${configId}`);
      if (defaultReportsClickHouseConfigId === configId) {
        setDefaultReportsClickHouseConfigIdState(null);
      }
      showMessage('Configuration removed successfully', 'success');
      fetchConfigurations();
    } catch (error: any) {
      showMessage(error.message || 'Failed to remove configuration', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSetDefaultReportServer = async (config: ModuleConfiguration) => {
    if (!config._id || config.moduleType !== 'ClickHouse') return;
    try {
      setLoading(true);
      await setDefaultReportsClickHouseConfigId(config._id);
      setDefaultReportsClickHouseConfigIdState(config._id);
      showMessage(`"${config.name}" is now the default ClickHouse server for Reports`, 'success');
    } catch (error: any) {
      showMessage(error.message || 'Failed to set default report server', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleAddHeader = () => {
    setFormData(prev => ({
      ...prev,
      httpHeaders: [...prev.httpHeaders, { key: '', value: '' }],
    }));
  };

  const handleRemoveHeader = (index: number) => {
    setFormData(prev => ({
      ...prev,
      httpHeaders: prev.httpHeaders.filter((_, i) => i !== index),
    }));
  };

  const handleHeaderChange = (index: number, field: 'key' | 'value', val: string) => {
    setFormData(prev => ({
      ...prev,
      httpHeaders: prev.httpHeaders.map((h, i) => i === index ? { ...h, [field]: val } : h),
    }));
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    resetLokiConnectionUi();
    resetCopyFromState();
  };

  const handleTestLokiConnection = async () => {
    if (!formData.url.trim()) return;
    setLokiTestLoading(true);
    setLokiTestSeverity('info');
    setLokiTestMessage('Testing connection to Loki…');
    try {
      const res = await apiClient.post('/il/settings/loki-test-connection', {
        moduleType: 'Loki',
        url: formData.url,
        authMethod: formData.authMethod,
        basicAuthUser: formData.basicAuthUser,
        basicAuthPassword: formData.basicAuthPassword,
        tlsSelfSignedCert: formData.tlsSelfSignedCert,
        tlsClientAuth: formData.tlsClientAuth,
        tlsSkipValidation: formData.tlsSkipValidation,
        httpHeaders: formData.httpHeaders,
        requestTimeout: formData.requestTimeout,
      });
      const payload = res.data as { success?: boolean; message?: string; labels?: string[] };
      if (payload.success) {
        const labels = payload.labels || [];
        setLokiTestSeverity('success');
        setLokiTestMessage(
          payload.message ||
            (labels.length > 0
              ? `Connection successful. ${labels.length} label(s) found — configure label values below.`
              : 'Connection successful. Loki returned no labels in the default time range.'),
        );
        setVerifiedLokiFingerprint(computeLokiConnectionFingerprint(formData));
        setFormData(prev => ({
          ...prev,
          labelValueSelections: {},
        }));
        setLokiLabelBrowserGeneration((g) => g + 1);
      } else {
        setLokiTestSeverity('error');
        setLokiTestMessage(payload.message || 'Connection failed.');
        setVerifiedLokiFingerprint(null);
      }
    } catch (e: any) {
      setLokiTestSeverity('error');
      setLokiTestMessage(e?.message || 'Connection test failed.');
      setVerifiedLokiFingerprint(null);
    } finally {
      setLokiTestLoading(false);
    }
  };

  const handleTestClickHouseConnection = async () => {
    if (!formData.url.trim()) return;
    setLokiTestLoading(true);
    setLokiTestSeverity('info');
    setLokiTestMessage('Testing connection to ClickHouse…');
    try {
      const res = await apiClient.post('/il/settings/clickhouse-test-connection', {
        moduleType: 'ClickHouse',
        url: formData.url,
        authMethod: formData.authMethod,
        basicAuthUser: formData.basicAuthUser,
        basicAuthPassword: formData.basicAuthPassword,
        tlsSelfSignedCert: formData.tlsSelfSignedCert,
        tlsClientAuth: formData.tlsClientAuth,
        tlsSkipValidation: formData.tlsSkipValidation,
        httpHeaders: formData.httpHeaders,
        requestTimeout: formData.requestTimeout,
        clusterEnabled: formData.clusterEnabled === true,
        clusterName: formData.clusterName || '',
      });
      const payload = res.data as {
        success?: boolean;
        message?: string;
        version?: string;
        databases?: string[];
        clusters?: Array<{ name: string; shards: number; replicas: number }>;
      };
      if (payload.success) {
        setLokiTestSeverity('success');
        setLokiTestMessage(payload.message || 'Connection successful.');
        setVerifiedLokiFingerprint(computeLokiConnectionFingerprint(formData));
      } else {
        setLokiTestSeverity('error');
        setLokiTestMessage(payload.message || 'Connection failed.');
        setVerifiedLokiFingerprint(null);
      }
    } catch (e: any) {
      setLokiTestSeverity('error');
      setLokiTestMessage(e?.message || 'Connection test failed.');
      setVerifiedLokiFingerprint(null);
    } finally {
      setLokiTestLoading(false);
    }
  };

  const handleTestConnection = () => {
    if (selectedModule === 'ClickHouse') return handleTestClickHouseConnection();
    return handleTestLokiConnection();
  };

  const filteredConfigurations = configurations.filter(config => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (
      config.name.toLowerCase().includes(q) ||
      config.moduleType.toLowerCase().includes(q) ||
      config.url.toLowerCase().includes(q)
    );
  });

  const handlePlatformTileClick = (moduleKey: string) => {
    resetLokiConnectionUi();
    resetCopyFromState();
    const base = MODULE_DEFAULT_FORMS[moduleKey] || defaultLokiForm;
    setFormData({ ...base, moduleType: moduleKey });
    setSelectedModule(moduleKey);
    setDialogMode('add');
    setEditingConfigId(null);
    setDialogOpen(true);
  };

  const handleNewConfigMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setNewConfigMenuAnchor(event.currentTarget);
  };

  const handleNewConfigMenuClose = () => {
    setNewConfigMenuAnchor(null);
  };

  const handleNewConfigTypeSelect = (moduleKey: string) => {
    handleNewConfigMenuClose();
    handlePlatformTileClick(moduleKey);
  };

  const addableModuleTypes = useMemo(
    () => MODULE_TYPES.filter((mt) => mt.available),
    [],
  );

  /**
   * Configs eligible to be copied from in the Add-config dialog: same
   * moduleType as the one being added, and not the placeholder itself.
   * Also exclude entries without an _id (those should never exist after a
   * round-trip to the server, but be defensive).
   */
  const copyableConfigs = useMemo<ModuleConfiguration[]>(() => {
    if (!selectedModule) return [];
    return configurations
      .filter((c) => c.moduleType === selectedModule && !!c._id)
      .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }, [configurations, selectedModule]);

  const copiedReportCount = useMemo(
    () => copiedReportSuites.reduce(
      (acc, s) => acc + (Array.isArray(s.reports) ? s.reports.length : 0),
      0,
    ),
    [copiedReportSuites],
  );

  /**
   * Apply (or clear) a "copy from existing" selection. Pre-fills every
   * connection/auth/TLS/headers/ClickHouse field from the source. The
   * destination name is seeded with "Copy of X" so admins must explicitly
   * rename. For ClickHouse we also stash the source's report suites tree
   * which gets forwarded to the server on save.
   *
   * NOTE: clears the verified-connection fingerprint so the admin must
   * re-run "Test connection" before Save unlocks — even if the URL/auth
   * didn't change, the new record needs its own verification stamp.
   */
  const handleCopyFromConfig = useCallback((source: ModuleConfiguration | null) => {
    if (!selectedModule) return;
    if (!source) {
      const base = MODULE_DEFAULT_FORMS[selectedModule] || defaultLokiForm;
      setFormData({ ...base, moduleType: selectedModule });
      resetCopyFromState();
      resetLokiConnectionUi();
      return;
    }
    setCopyFromConfigId(source._id || null);
    setFormData({
      moduleType: selectedModule,
      name: `Copy of ${source.name || ''}`.trim(),
      url: source.url || '',
      authMethod: source.authMethod || 'none',
      basicAuthUser: source.basicAuthUser || '',
      basicAuthPassword: source.basicAuthPassword || '',
      tlsSelfSignedCert: !!source.tlsSelfSignedCert,
      tlsClientAuth: !!source.tlsClientAuth,
      tlsSkipValidation: !!source.tlsSkipValidation,
      httpHeaders: Array.isArray(source.httpHeaders)
        ? source.httpHeaders.map((h) => ({ key: h.key || '', value: h.value || '' }))
        : [],
      requestTimeout: source.requestTimeout || 30,
      allowedCookies: source.allowedCookies || '',
      labelValueSelections: source.labelValueSelections && typeof source.labelValueSelections === 'object'
        ? { ...source.labelValueSelections }
        : {},
      tenantId: source.tenantId || '',
      siteId: source.siteId || '',
      dbName: source.dbName || '',
      clusterEnabled: source.clusterEnabled === true,
      clusterName: source.clusterName || '',
      preciseVisitorLocation: source.preciseVisitorLocation === true,
      defaultTimeRange: source.defaultTimeRange || '',
      assignedUserIds: Array.isArray(source.assignedUserIds)
        ? source.assignedUserIds.map(String)
        : [],
    });
    if (selectedModule === 'ClickHouse') {
      setCopiedReportSuites(Array.isArray(source.reportSuites) ? source.reportSuites : []);
    } else {
      setCopiedReportSuites([]);
    }
    resetLokiConnectionUi();
  }, [selectedModule, resetCopyFromState, resetLokiConnectionUi]);

  const renderCopyFromPicker = () => {
    if (dialogMode !== 'add' || !selectedModule) return null;
    if (copyableConfigs.length === 0) return null;
    const selected = copyFromConfigId
      ? copyableConfigs.find((c) => c._id === copyFromConfigId) || null
      : null;
    const isClickHouse = selectedModule === 'ClickHouse';
    const moduleLabel = MODULE_TYPES.find((m) => m.key === selectedModule)?.label || selectedModule;
    return (
      <Box sx={{ mb: 2.5 }}>
        <Autocomplete
          size="small"
          options={copyableConfigs}
          value={selected}
          onChange={(_, value) => handleCopyFromConfig(value)}
          getOptionLabel={(o) => {
            if (!o) return '';
            const extra = o.moduleType === 'ClickHouse' && o.dbName
              ? ` — ${o.dbName}`
              : (o.url ? ` — ${o.url}` : '');
            return `${o.name}${extra}`;
          }}
          isOptionEqualToValue={(a, b) => (a?._id || '') === (b?._id || '')}
          renderInput={(params) => (
            <TextField
              {...params}
              label={`Copy from existing ${moduleLabel} (optional)`}
              placeholder={`Select a previously saved ${moduleLabel} to pre-fill the form`}
              helperText={
                isClickHouse
                  ? 'ClickHouse copies are deep — report suites and reports under them are duplicated too. You can edit any field before saving.'
                  : 'All connection / auth fields are copied. You can edit any field before saving.'
              }
            />
          )}
        />
        {selected && isClickHouse && (
          <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
            <Chip
              size="small"
              color="info"
              variant="outlined"
              label={
                copiedReportSuites.length === 0
                  ? `Source "${selected.name}" has no report suites — only connection settings will be copied`
                  : `Will deep-copy ${copiedReportSuites.length} report suite(s) · ${copiedReportCount} report panel(s)`
              }
            />
            <Chip
              size="small"
              variant="outlined"
              label="Rename the destination before saving"
              color={(formData.name || '').trim().toLowerCase() === `copy of ${(selected.name || '').toLowerCase()}` ? 'warning' : 'default'}
            />
          </Box>
        )}
      </Box>
    );
  };

  const handleViewConfig = useCallback((config: ModuleConfiguration) => {
    resetLokiConnectionUi();
    setFormData({
      moduleType: config.moduleType,
      name: config.name,
      url: config.url,
      authMethod: config.authMethod,
      basicAuthUser: config.basicAuthUser,
      basicAuthPassword: config.basicAuthPassword,
      tlsSelfSignedCert: config.tlsSelfSignedCert,
      tlsClientAuth: config.tlsClientAuth,
      tlsSkipValidation: config.tlsSkipValidation,
      httpHeaders: config.httpHeaders || [],
      requestTimeout: config.requestTimeout,
      allowedCookies: config.allowedCookies,
      labelValueSelections: config.labelValueSelections && typeof config.labelValueSelections === 'object'
        ? { ...config.labelValueSelections }
        : {},
      tenantId: config.tenantId || '',
      siteId: config.siteId || '',
      dbName: config.dbName || '',
      clusterEnabled: config.clusterEnabled === true,
      clusterName: config.clusterName || '',
      preciseVisitorLocation: config.preciseVisitorLocation === true,
      defaultTimeRange: config.defaultTimeRange || '',
      assignedUserIds: Array.isArray(config.assignedUserIds)
        ? config.assignedUserIds.map(String)
        : [],
    });
    setSelectedModule(config.moduleType);
    setDialogMode('view');
    setEditingConfigId(config._id || null);
    setDialogOpen(true);
  }, [resetLokiConnectionUi]);

  const handleEditConfig = (config: ModuleConfiguration) => {
    const nextForm: Omit<ModuleConfiguration, '_id' | 'createdAt' | 'updatedAt'> = {
      moduleType: config.moduleType,
      name: config.name,
      url: config.url,
      authMethod: config.authMethod,
      basicAuthUser: config.basicAuthUser,
      basicAuthPassword: config.basicAuthPassword,
      tlsSelfSignedCert: config.tlsSelfSignedCert,
      tlsClientAuth: config.tlsClientAuth,
      tlsSkipValidation: config.tlsSkipValidation,
      httpHeaders: config.httpHeaders || [],
      requestTimeout: config.requestTimeout,
      allowedCookies: config.allowedCookies,
      labelValueSelections: config.labelValueSelections && typeof config.labelValueSelections === 'object'
        ? { ...config.labelValueSelections }
        : {},
      tenantId: config.tenantId || '',
      siteId: config.siteId || '',
      dbName: config.dbName || '',
      clusterEnabled: config.clusterEnabled === true,
      clusterName: config.clusterName || '',
      preciseVisitorLocation: config.preciseVisitorLocation === true,
      defaultTimeRange: config.defaultTimeRange || '',
      assignedUserIds: Array.isArray(config.assignedUserIds)
        ? config.assignedUserIds.map(String)
        : [],
    };
    setFormData(nextForm);
    setLokiTestMessage('');
    setLokiTestSeverity(null);
    setLokiTestLoading(false);
    setLokiLabelBrowserGeneration(0);
    setVerifiedLokiFingerprint(
      (config.moduleType === 'Loki' || config.moduleType === 'ClickHouse')
        ? computeLokiConnectionFingerprint(nextForm)
        : null,
    );
    setSelectedModule(config.moduleType);
    setDialogMode('edit');
    setEditingConfigId(config._id || null);
    setDialogOpen(true);
  };

  const handleSettingsTileTouchStart = useCallback(
    (e: React.TouchEvent, config: ModuleConfiguration) => {
      if (!isMobile) return;
      const t = e.touches[0];
      const id = settingsConfigTileId(config);
      setSettingsTileSwipeRow((prev) => {
        const anotherWasOpen = Boolean(
          prev && prev.tileId !== id && Math.abs(prev.offset) > 0.5
        );
        const baseOffset =
          !anotherWasOpen && prev?.tileId === id ? prev!.offset : 0;
        settingsSwipeSessionRef.current = {
          tileId: id,
          startX: t.clientX,
          startY: t.clientY,
          baseOffset,
          lock: null,
        };
        settingsLatestSwipeOffsetRef.current = baseOffset;
        return { tileId: id, offset: baseOffset, dragging: true };
      });
    },
    [isMobile]
  );

  const handleSettingsTileTouchMove = useCallback(
    (e: React.TouchEvent, config: ModuleConfiguration, rowCanEdit: boolean, rowCanDelete: boolean) => {
      if (!isMobile) return;
      const id = settingsConfigTileId(config);
      const sess = settingsSwipeSessionRef.current;
      if (!sess || sess.tileId !== id) return;
      const touch = e.touches[0];
      const dx = touch.clientX - sess.startX;
      const dy = touch.clientY - sess.startY;

      if (sess.lock === null) {
        if (
          Math.abs(dx) > SWIPE_DIRECTION_LOCK_PX &&
          Math.abs(dx) > Math.abs(dy) * 1.2
        ) {
          sess.lock = 'h';
        } else if (
          Math.abs(dy) > SWIPE_DIRECTION_LOCK_PX &&
          Math.abs(dy) > Math.abs(dx) * 1.2
        ) {
          sess.lock = 'v';
        }
      }
      if (sess.lock !== 'h') return;

      const raw = sess.baseOffset + dx;
      const clamped = clampSwipeOffset(
        raw,
        rowCanEdit,
        rowCanDelete,
        SWIPE_ACTION_WIDTH_PX
      );
      settingsLatestSwipeOffsetRef.current = clamped;
      setSettingsTileSwipeRow({
        tileId: id,
        offset: clamped,
        dragging: true,
      });
    },
    [isMobile]
  );

  const handleSettingsTileTouchEnd = useCallback(
    (
      e: React.TouchEvent,
      config: ModuleConfiguration,
      rowCanEdit: boolean,
      rowCanDelete: boolean
    ) => {
      if (!isMobile) return;
      const id = settingsConfigTileId(config);
      const sess = settingsSwipeSessionRef.current;
      if (!sess || sess.tileId !== id) {
        settingsSwipeSessionRef.current = null;
        return;
      }
      const t = e.changedTouches[0];
      const dx = t.clientX - sess.startX;
      const dy = t.clientY - sess.startY;
      const { lock, baseOffset } = sess;
      settingsSwipeSessionRef.current = null;

      const finishHorizontalSnap = (off: number) => {
        const snapped = computeSnappedOffset(
          off,
          rowCanEdit,
          rowCanDelete,
          SWIPE_ACTION_WIDTH_PX
        );
        settingsLatestSwipeOffsetRef.current = snapped;
        setSettingsTileSwipeRow({
          tileId: id,
          offset: snapped,
          dragging: false,
        });
      };

      if (
        lock === null &&
        Math.abs(dx) > Math.abs(dy) * 1.2 &&
        Math.abs(dx) > 18
      ) {
        const raw = baseOffset + dx;
        const clamped = clampSwipeOffset(
          raw,
          rowCanEdit,
          rowCanDelete,
          SWIPE_ACTION_WIDTH_PX
        );
        finishHorizontalSnap(clamped);
        return;
      }

      if (lock !== 'h') {
        if (Math.abs(dx) < 12 && Math.abs(dy) < 12) {
          if (Math.abs(baseOffset) > 0.5) {
            settingsLatestSwipeOffsetRef.current = 0;
            setSettingsTileSwipeRow({
              tileId: id,
              offset: 0,
              dragging: false,
            });
          } else {
            handleViewConfig(config);
          }
        } else {
          settingsLatestSwipeOffsetRef.current = baseOffset;
          setSettingsTileSwipeRow({
            tileId: id,
            offset: baseOffset,
            dragging: false,
          });
        }
        return;
      }

      finishHorizontalSnap(settingsLatestSwipeOffsetRef.current);
    },
    [isMobile, handleViewConfig]
  );

  const handleSettingsTileTouchCancel = useCallback(
    (
      _e: React.TouchEvent,
      config: ModuleConfiguration,
      rowCanEdit: boolean,
      rowCanDelete: boolean
    ) => {
      if (!isMobile) return;
      const id = settingsConfigTileId(config);
      const sess = settingsSwipeSessionRef.current;
      if (!sess || sess.tileId !== id) return;
      settingsSwipeSessionRef.current = null;
      const snapped = computeSnappedOffset(
        settingsLatestSwipeOffsetRef.current,
        rowCanEdit,
        rowCanDelete,
        SWIPE_ACTION_WIDTH_PX
      );
      settingsLatestSwipeOffsetRef.current = snapped;
      setSettingsTileSwipeRow({
        tileId: id,
        offset: snapped,
        dragging: false,
      });
    },
    [isMobile]
  );

  const renderConfiguredTile = (config: ModuleConfiguration) => {
    const logoFile = MODULE_LOGO_MAP[config.moduleType] || 'loki_logo.svg';
    const moduleLabel = MODULE_TYPES.find(m => m.key === config.moduleType)?.label || config.moduleType;
    const tileId = settingsConfigTileId(config);
    const canEdit = true;
    const canDelete = Boolean(config._id);
    const showActionButtons = canEdit || canDelete;
    const isDefaultReportServer = config.moduleType === 'ClickHouse'
      && Boolean(config._id)
      && defaultReportsClickHouseConfigId === config._id;

    const defaultReportBadge = isDefaultReportServer ? (
      <Tooltip title="Default ClickHouse server for Reports">
        <Box
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            zIndex: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 28,
            height: 28,
            borderRadius: '50%',
            bgcolor: 'success.main',
            boxShadow: '0 2px 6px rgba(0,0,0,0.25)',
          }}
        >
          <CheckCircle sx={{ fontSize: 18, color: 'success.contrastText' }} />
        </Box>
      </Tooltip>
    ) : null;

    const rowOffset =
      isMobile && settingsTileSwipeRow?.tileId === tileId
        ? settingsTileSwipeRow!.offset
        : 0;
    const rowDragging =
      isMobile &&
      settingsTileSwipeRow?.tileId === tileId &&
      settingsTileSwipeRow!.dragging;

    const gradientFill = (
      <Box sx={{
        position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
        background: configTileGradient,
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 0.25,
      }}>
        <ModuleLogo src={logoFile} alt={moduleLabel} size={44} />
        <Chip
          label={moduleLabel}
          size="small"
          sx={{
            mt: 0.25,
            height: 20,
            fontSize: '0.62rem',
            fontWeight: 700,
            color: configTileModuleBadgeColor,
            backgroundColor: theme.palette.dashboard?.heroTileForegroundColor
              ? 'rgba(25, 25, 25, 0.08)'
              : 'rgba(255,255,255,0.18)',
          }}
        />
      </Box>
    );

    const overlayPanel = (
      <Box
        className="cfg-tile-overlay"
        sx={{
          position: 'absolute', bottom: 0, left: 0, right: 0,
          overflow: 'hidden',
          background: 'linear-gradient(to top, rgba(0,0,0,0.88) 0%, rgba(0,0,0,0.72) 50%, rgba(0,0,0,0.25) 100%)',
          transition: 'height 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
          height: 36, px: 1.25,
          display: 'flex', flexDirection: 'column', justifyContent: 'flex-start',
        }}
      >
        <Typography variant="caption" noWrap className="cfg-tile-name-short" sx={{
          color: '#fff', fontWeight: 700, fontSize: '0.75rem',
          lineHeight: '36px', textShadow: '0 1px 3px rgba(0,0,0,0.4)', flexShrink: 0,
          transition: 'opacity 0.2s ease, height 0.2s ease, line-height 0.2s ease',
        }}>
          {config.name}
        </Typography>

        <Box className="cfg-tile-details" sx={{
          opacity: 0, transition: 'opacity 0.3s ease 0.08s',
          display: 'flex', flexDirection: 'column', gap: 0.5, pt: 0.25, pb: 1, flex: 1,
        }}>
          <Typography variant="caption" sx={{
            color: '#fff', fontWeight: 700, fontSize: '0.72rem', lineHeight: 1.2,
            textShadow: '0 1px 3px rgba(0,0,0,0.4)', wordBreak: 'break-word',
          }}>
            {config.name}
          </Typography>
          <Typography variant="caption" noWrap sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.62rem', lineHeight: 1.2 }}>
            {config.url}
          </Typography>
          {showActionButtons && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 'auto' }}>
              <Box display="flex" gap={0.5}>
                {canEdit && (
                  <Tooltip title="Edit">
                    <Button color="secondary" variant="contained" size="small"
                      onClick={(e) => { e.stopPropagation(); handleEditConfig(config); }}
                      sx={{ borderRadius: 1, textTransform: 'none', px: 1.25, minWidth: 36 }}>
                      <Edit sx={{ fontSize: 16 }} />
                    </Button>
                  </Tooltip>
                )}
                {config.moduleType === 'ClickHouse' && (
                  <Tooltip title="Report Suites">
                    <Button color="info" variant="contained" size="small"
                      onClick={(e) => { e.stopPropagation(); navigate(`/infralytiqs/settings/${config._id}`); }}
                      sx={{ borderRadius: 1, textTransform: 'none', px: 1.25, minWidth: 36 }}>
                      <AssessmentOutlined sx={{ fontSize: 16 }} />
                    </Button>
                  </Tooltip>
                )}
                {config.moduleType === 'ClickHouse' && canAccessInfralytiqsSettings && !isDefaultReportServer && (
                  <Tooltip title="Set as default ClickHouse server for Reports">
                    <Button color="success" variant="outlined" size="small"
                      onClick={(e) => { e.stopPropagation(); void handleSetDefaultReportServer(config); }}
                      sx={{ borderRadius: 1, textTransform: 'none', px: 1.25, minWidth: 36 }}>
                      <CheckCircle sx={{ fontSize: 16 }} />
                    </Button>
                  </Tooltip>
                )}
              </Box>
              <Box display="flex" gap={0.5}>
                {canDelete && (
                  <Tooltip title="Delete">
                    <Button color="error" variant="contained" size="small"
                      onClick={(e) => { e.stopPropagation(); handleDelete(config._id!); }}
                      sx={{ borderRadius: 1, textTransform: 'none', px: 1.25, minWidth: 36 }}>
                      <Delete sx={{ fontSize: 16 }} />
                    </Button>
                  </Tooltip>
                )}
              </Box>
            </Box>
          )}
        </Box>
      </Box>
    );

    if (!isMobile) {
      return (
        <Box
          key={tileId}
          onClick={() => handleViewConfig(config)}
          sx={{
            width: 190,
            height: 150,
            borderRadius: 2,
            overflow: 'hidden',
            position: 'relative',
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
            flexShrink: 0,
            cursor: 'pointer',
            '&:hover .cfg-tile-overlay': { height: 100 },
            '&:hover .cfg-tile-details': { opacity: 1 },
            '&:hover .cfg-tile-name-short': { opacity: 0, height: 0, lineHeight: 0 },
            '@media (hover: none)': {
              '& .cfg-tile-overlay': { height: 100 },
              '& .cfg-tile-details': { opacity: 1 },
              '& .cfg-tile-name-short': { opacity: 0, height: 0, lineHeight: 0 },
            },
          }}
        >
          {gradientFill}
          {defaultReportBadge}
          {overlayPanel}
        </Box>
      );
    }

    return (
      <Box
        key={tileId}
        sx={{
          width: 190,
          height: 150,
          borderRadius: 2,
          overflow: 'hidden',
          position: 'relative',
          flexShrink: 0,
          boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
          touchAction: 'pan-y',
          isolation: 'isolate',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'stretch',
            zIndex: 0,
            borderRadius: 2,
            overflow: 'hidden',
          }}
        >
          {canEdit && (
            <Box
              sx={{
                width: SWIPE_ACTION_WIDTH_PX,
                flexShrink: 0,
                bgcolor: 'secondary.main',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Tooltip title="Edit">
                <IconButton
                  aria-label="Edit configuration"
                  size="medium"
                  sx={{
                    color: 'secondary.contrastText',
                    '&:hover': { backgroundColor: 'rgba(0,0,0,0.12)' },
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    setSettingsTileSwipeRow(null);
                    handleEditConfig(config);
                  }}
                >
                  <Edit fontSize="medium" />
                </IconButton>
              </Tooltip>
            </Box>
          )}
          {config.moduleType === 'ClickHouse' && (
            <Box
              sx={{
                width: SWIPE_ACTION_WIDTH_PX,
                flexShrink: 0,
                bgcolor: 'info.main',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Tooltip title="Report Suites">
                <IconButton
                  aria-label="Report Suites"
                  size="medium"
                  sx={{
                    color: 'info.contrastText',
                    '&:hover': { backgroundColor: 'rgba(0,0,0,0.12)' },
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    setSettingsTileSwipeRow(null);
                    navigate(`/infralytiqs/settings/${config._id}`);
                  }}
                >
                  <AssessmentOutlined fontSize="medium" />
                </IconButton>
              </Tooltip>
            </Box>
          )}
          <Box sx={{ flex: 1, minWidth: 0 }} />
          {canDelete && (
            <Box
              sx={{
                width: SWIPE_ACTION_WIDTH_PX,
                flexShrink: 0,
                bgcolor: 'error.main',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Tooltip title="Delete">
                <IconButton
                  aria-label="Delete configuration"
                  size="medium"
                  sx={{
                    color: 'error.contrastText',
                    '&:hover': { backgroundColor: 'rgba(0,0,0,0.12)' },
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    setSettingsTileSwipeRow(null);
                    handleDelete(config._id!);
                  }}
                >
                  <Delete fontSize="medium" />
                </IconButton>
              </Tooltip>
            </Box>
          )}
        </Box>

        <Box
          onTouchStart={(e) => handleSettingsTileTouchStart(e, config)}
          onTouchMove={(e) => handleSettingsTileTouchMove(e, config, canEdit, canDelete)}
          onTouchEnd={(e) => handleSettingsTileTouchEnd(e, config, canEdit, canDelete)}
          onTouchCancel={(e) => handleSettingsTileTouchCancel(e, config, canEdit, canDelete)}
          sx={{
            position: 'relative',
            zIndex: 1,
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            borderRadius: 2,
            overflow: 'hidden',
            cursor: 'pointer',
            backgroundColor: 'background.paper',
            transform: `translateX(${rowOffset}px)`,
            transition: rowDragging
              ? 'none'
              : 'transform 0.35s cubic-bezier(0.25, 0.8, 0.25, 1)',
            willChange: rowDragging ? 'transform' : 'auto',
            boxShadow: rowOffset !== 0 ? 3 : 'none',
          }}
        >
          <Box sx={{ flex: 1, minHeight: 0, position: 'relative' }}>
            {gradientFill}
            {defaultReportBadge}
          </Box>
          <Box sx={{ position: 'relative', flexShrink: 0 }}>
            <Box
              sx={{
                background: 'linear-gradient(to top, rgba(0,0,0,0.92) 0%, rgba(0,0,0,0.75) 55%, rgba(0,0,0,0.2) 100%)',
                px: 1.25,
                py: 1,
                minHeight: 44,
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  color: '#fff',
                  fontWeight: 700,
                  fontSize: '0.75rem',
                  lineHeight: 1.35,
                  textShadow: '0 1px 4px rgba(0,0,0,0.45)',
                  wordBreak: 'break-word',
                }}
              >
                {config.name}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>
    );
  };

  const isReadOnly = dialogMode === 'view';

  const currentLokiFingerprint = computeLokiConnectionFingerprint(formData);
  const lokiConnectionVerified =
    verifiedLokiFingerprint !== null && verifiedLokiFingerprint === currentLokiFingerprint;

  const lokiBrowserInstanceKey = `${verifiedLokiFingerprint || ''}-${lokiLabelBrowserGeneration}-${editingConfigId || 'new'}`;

  const getLokiConnectionBodyForPreview = useCallback(
    () => ({
      moduleType: formData.moduleType,
      url: formData.url,
      authMethod: formData.authMethod,
      basicAuthUser: formData.basicAuthUser,
      basicAuthPassword: formData.basicAuthPassword,
      tlsSelfSignedCert: formData.tlsSelfSignedCert,
      tlsClientAuth: formData.tlsClientAuth,
      tlsSkipValidation: formData.tlsSkipValidation,
      httpHeaders: formData.httpHeaders,
      requestTimeout: formData.requestTimeout,
    }),
    [
      formData.moduleType,
      formData.url,
      formData.authMethod,
      formData.basicAuthUser,
      formData.basicAuthPassword,
      formData.tlsSelfSignedCert,
      formData.tlsClientAuth,
      formData.tlsSkipValidation,
      formData.httpHeaders,
      formData.requestTimeout,
    ],
  );

  const assignedUserAutocompleteValue = useMemo(() => {
    const ids = formData.assignedUserIds || [];
    const byId = new Map(companyMembers.map((m) => [m._id, m]));
    return ids.map((id) => byId.get(id) || { _id: id, email: '', label: id });
  }, [companyMembers, formData.assignedUserIds]);

  const renderTenantIdField = (options?: { required?: boolean }) => {
    const selectedValue = formData.tenantId || '';
    const hasLegacyValue = Boolean(
      selectedValue && !ilTenants.some((tenant) => tenant.tenantName === selectedValue),
    );

    return (
      <FormControl
        size="small"
        required={options?.required}
        sx={{ flex: '1 1 calc(50% - 8px)', minWidth: 200 }}
        disabled={isReadOnly}
      >
        <InputLabel>Tenant Id</InputLabel>
        <Select
          label="Tenant Id"
          value={selectedValue}
          onChange={(e) => setFormData((prev) => ({ ...prev, tenantId: e.target.value }))}
        >
          <MenuItem value="">
            <em>{ilTenants.length === 0 ? 'No tenants configured' : 'Select a tenant'}</em>
          </MenuItem>
          {hasLegacyValue && (
            <MenuItem value={selectedValue}>{selectedValue}</MenuItem>
          )}
          {ilTenants.map((tenant) => (
            <MenuItem key={tenant._id} value={tenant.tenantName}>
              {tenant.tenantName}
            </MenuItem>
          ))}
        </Select>
        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, ml: 1.5 }}>
          {ilTenants.length === 0
            ? 'Add tenants in the Tenants accordion above before linking this server.'
            : 'Tenant name from IL-Tenants saved as the analytics tenant identifier'}
        </Typography>
      </FormControl>
    );
  };

  const renderLokiForm = () => (
    <Box sx={{ mt: 1 }}>
      {renderCopyFromPicker()}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
        <TextField
          label="Name of the Server"
          value={formData.name}
          onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
          required
          size="small"
          sx={{ flex: '1 1 calc(50% - 8px)', minWidth: 200 }}
          disabled={isReadOnly}
        />
        <TextField
          label="URL"
          placeholder="http://localhost:3100"
          value={formData.url}
          onChange={(e) => setFormData(prev => ({ ...prev, url: e.target.value }))}
          required
          size="small"
          sx={{ flex: '1 1 calc(50% - 8px)', minWidth: 200 }}
          disabled={isReadOnly}
        />
        {renderTenantIdField()}
        <FormControl size="small" sx={{ flex: '1 1 calc(50% - 8px)', minWidth: 200 }} disabled={isReadOnly}>
          <InputLabel>Authentication Method</InputLabel>
          <Select
            value={formData.authMethod}
            label="Authentication Method"
            onChange={(e) => setFormData(prev => ({ ...prev, authMethod: e.target.value }))}
          >
            <MenuItem value="none">No Authentication</MenuItem>
            <MenuItem value="basic">Basic Authentication</MenuItem>
          </Select>
        </FormControl>
        {formData.authMethod === 'basic' && (
          <>
            <TextField
              label="Username"
              value={formData.basicAuthUser}
              onChange={(e) => setFormData(prev => ({ ...prev, basicAuthUser: e.target.value }))}
              size="small"
              sx={{ flex: '1 1 calc(50% - 8px)', minWidth: 200 }}
              disabled={isReadOnly}
            />
            <TextField
              label="Password"
              type="password"
              value={formData.basicAuthPassword}
              onChange={(e) => setFormData(prev => ({ ...prev, basicAuthPassword: e.target.value }))}
              size="small"
              sx={{ flex: '1 1 calc(50% - 8px)', minWidth: 200 }}
              disabled={isReadOnly}
            />
          </>
        )}
      </Box>

      {/* TLS Settings */}
      <Box sx={{ mt: 2.5 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5, color: 'text.secondary' }}>
          TLS Settings
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', pl: 1 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={formData.tlsSelfSignedCert}
                onChange={(e) => setFormData(prev => ({ ...prev, tlsSelfSignedCert: e.target.checked }))}
                size="small"
                disabled={isReadOnly}
              />
            }
            label="Add Self-signed certificate"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={formData.tlsClientAuth}
                onChange={(e) => setFormData(prev => ({ ...prev, tlsClientAuth: e.target.checked }))}
                size="small"
                disabled={isReadOnly}
              />
            }
            label="TLS client Authentication"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={formData.tlsSkipValidation}
                onChange={(e) => setFormData(prev => ({ ...prev, tlsSkipValidation: e.target.checked }))}
                size="small"
                disabled={isReadOnly}
              />
            }
            label="Skip TLS Certificate validation"
          />
        </Box>
      </Box>

      {/* HTTP Headers */}
      <Box sx={{ mt: 2.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
            HTTP Headers
          </Typography>
          {!isReadOnly && (
            <Button size="small" variant="outlined" color="secondary" startIcon={<Add />} onClick={handleAddHeader}>
              Add Header
            </Button>
          )}
        </Box>
        {formData.httpHeaders.map((header, index) => (
          <Box key={index} sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center' }}>
            <TextField
              size="small"
              label="Header Key"
              value={header.key}
              onChange={(e) => handleHeaderChange(index, 'key', e.target.value)}
              sx={{ flex: 1 }}
              disabled={isReadOnly}
            />
            <TextField
              size="small"
              label="Header Value"
              value={header.value}
              onChange={(e) => handleHeaderChange(index, 'value', e.target.value)}
              sx={{ flex: 1 }}
              disabled={isReadOnly}
            />
            {!isReadOnly && (
              <IconButton size="small" color="error" onClick={() => handleRemoveHeader(index)}>
                <RemoveCircleOutlineIcon fontSize="small" />
              </IconButton>
            )}
          </Box>
        ))}
        {formData.httpHeaders.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ pl: 1 }}>
            No custom headers configured.
          </Typography>
        )}
      </Box>

      {/* Timeout & Cookies */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 2.5 }}>
        <TextField
          label="Request Timeout (seconds)"
          type="number"
          value={formData.requestTimeout}
          onChange={(e) => setFormData(prev => ({ ...prev, requestTimeout: parseInt(e.target.value) || 30 }))}
          size="small"
          sx={{ flex: '1 1 calc(50% - 8px)', minWidth: 200 }}
          inputProps={{ min: 1, max: 300 }}
          disabled={isReadOnly}
        />
        <TextField
          label="Allowed Cookie Names"
          placeholder="cookie1, cookie2"
          value={formData.allowedCookies}
          onChange={(e) => setFormData(prev => ({ ...prev, allowedCookies: e.target.value }))}
          size="small"
          sx={{ flex: '1 1 calc(50% - 8px)', minWidth: 200 }}
          disabled={isReadOnly}
        />
      </Box>

      {showCompanyUserAssignPicker && (
        <Box sx={{ mt: 2.5 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5, color: 'text.secondary' }}>
            Assigned users
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
            Search below to add users. Selected users appear as tiles under the field so large lists stay readable.
          </Typography>
          <Autocomplete
            multiple
            disableCloseOnSelect
            disabled={isReadOnly}
            options={companyMembers}
            getOptionLabel={(option) => option.label}
            isOptionEqualToValue={(a, b) => a._id === b._id}
            value={assignedUserAutocompleteValue}
            onChange={(_, newValue) => {
              setFormData((prev) => ({
                ...prev,
                assignedUserIds: newValue.map((m) => m._id),
              }));
            }}
            renderTags={() => null}
            renderInput={(params) => (
              <TextField {...params} size="small" label="Add users" placeholder="Search and select" />
            )}
          />
          {(assignedUserAutocompleteValue.length > 0 || !isReadOnly) && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                Selected users ({assignedUserAutocompleteValue.length})
              </Typography>
              {assignedUserAutocompleteValue.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No users selected yet.
                </Typography>
              ) : (
                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 1,
                    maxHeight: 280,
                    overflowY: 'auto',
                    pr: 0.5,
                  }}
                >
                  {assignedUserAutocompleteValue.map((member) => (
                    <Paper
                      key={member._id}
                      variant="outlined"
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 0.75,
                        py: 0.75,
                        px: 1.25,
                        borderRadius: 2,
                        maxWidth: '100%',
                        borderWidth: 1,
                        borderStyle: 'solid',
                        borderColor: 'secondary.main',
                      }}
                    >
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography variant="body2" fontWeight={600} noWrap title={member.label}>
                          {member.label}
                        </Typography>
                        {member.email ? (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            noWrap
                            display="block"
                            title={member.email}
                          >
                            {member.email}
                          </Typography>
                        ) : null}
                      </Box>
                      {!isReadOnly && (
                        <Button
                          variant="contained"
                          color="error"
                          size="small"
                          aria-label={`Remove ${member.label}`}
                          onClick={() => {
                            setFormData((prev) => ({
                              ...prev,
                              assignedUserIds: (prev.assignedUserIds || []).filter((id) => id !== member._id),
                            }));
                          }}
                          sx={{
                            flexShrink: 0,
                            ml: 0.25,
                            minWidth: 32,
                            width: 32,
                            height: 32,
                            p: 0,
                            borderRadius: 1,
                            bgcolor: 'error.main',
                            color: 'error.contrastText',
                            boxShadow: 'none',
                            '&:hover': {
                              bgcolor: 'error.dark',
                              boxShadow: 'none',
                            },
                          }}
                        >
                          <CloseIcon sx={{ fontSize: 18, color: 'inherit' }} />
                        </Button>
                      )}
                    </Paper>
                  ))}
                </Box>
              )}
            </Box>
          )}
        </Box>
      )}

      {!isReadOnly && (
        <Box sx={{ mt: 2.5, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            color="secondary"
            startIcon={
              lokiTestLoading ? (
                <CircularProgress size={18} color="inherit" />
              ) : (
                <NetworkCheck />
              )
            }
            onClick={handleTestLokiConnection}
            disabled={lokiTestLoading || !formData.url.trim()}
            sx={{
              borderColor: 'secondary.main',
              color: 'secondary.main',
              '&:hover': { borderColor: 'secondary.dark', color: 'secondary.dark' },
              '&.Mui-disabled': (t) => (t.palette.mode === 'light'
                ? {
                  borderColor: 'rgba(25, 25, 25, 0.38)',
                  color: 'rgb(25, 25, 25)',
                  backgroundColor: 'rgba(25, 25, 25, 0.04)',
                }
                : {}),
            }}
          >
            Test connection
          </Button>
          <Typography variant="caption" color="text.secondary">
            Run a test before saving. Changing URL, auth, TLS, headers, or timeout requires a new test.
          </Typography>
        </Box>
      )}

      {lokiTestSeverity && (
        <Alert severity={lokiTestSeverity === 'success' ? 'success' : lokiTestSeverity === 'error' ? 'error' : 'info'} sx={{ mt: 2 }}>
          {lokiTestMessage}
        </Alert>
      )}

      {!isReadOnly && lokiConnectionVerified && (
        <Box
          sx={{
            mt: 2,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2,
            p: 2,
            backgroundColor: (t) => (t.palette.mode === 'dark' ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)'),
          }}
        >
          <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5, color: 'text.secondary' }}>
            Label filters (optional)
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
            Same layout as Infralytiqs Logs → Labels. Select label names, then choose values. Your selections are saved with this Loki server.
          </Typography>
          <LokiSettingsLabelBrowser
            instanceKey={lokiBrowserInstanceKey}
            getConnectionBody={getLokiConnectionBodyForPreview}
            selections={formData.labelValueSelections || {}}
            onSelectionsChange={(next) =>
              setFormData((prev) => ({ ...prev, labelValueSelections: next }))
            }
          />
        </Box>
      )}

      {isReadOnly &&
        formData.labelValueSelections &&
        Object.keys(formData.labelValueSelections).length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: 'text.secondary' }}>
              Saved label filters
            </Typography>
            {Object.entries(formData.labelValueSelections).map(([label, values]) => (
                <Box key={label} sx={{ mb: 1.5 }}>
                  <Typography variant="caption" fontWeight={700} display="block" color="text.secondary">
                    {label}
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                    {(values || []).length > 0 ? (
                      (values || []).map((v) => (
                        <Chip key={`${label}:${v}`} label={v} size="small" variant="outlined" />
                      ))
                    ) : (
                      <Chip label="All values" size="small" variant="outlined" />
                    )}
                  </Box>
                </Box>
              ))}
          </Box>
        )}
    </Box>
  );

  const renderClickHouseForm = () => (
    <Box sx={{ mt: 1 }}>
      {renderCopyFromPicker()}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
        <TextField
          label="Name of the Server"
          value={formData.name}
          onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
          required
          size="small"
          sx={{ flex: '1 1 calc(50% - 8px)', minWidth: 200 }}
          disabled={isReadOnly}
        />
        <TextField
          label="URL"
          placeholder="http://localhost:8123"
          value={formData.url}
          onChange={(e) => setFormData(prev => ({ ...prev, url: e.target.value }))}
          required
          size="small"
          sx={{ flex: '1 1 calc(50% - 8px)', minWidth: 200 }}
          disabled={isReadOnly}
        />
        {renderTenantIdField({ required: true })}
        <TextField
          label="Site ID"
          placeholder="e.g. www-example-com (optional — leave blank for all sites)"
          value={formData.siteId || ''}
          onChange={(e) => setFormData(prev => ({ ...prev, siteId: e.target.value }))}
          size="small"
          sx={{ flex: '1 1 calc(50% - 8px)', minWidth: 200 }}
          disabled={isReadOnly}
          helperText="Per-site scope for ingest and APIs, or leave empty to run report suites across every site_id for this tenant and DB"
        />
        <TextField
          label="DB Name"
          placeholder="e.g. analytics_my_company"
          value={formData.dbName || ''}
          onChange={(e) => setFormData(prev => ({ ...prev, dbName: e.target.value }))}
          required
          size="small"
          sx={{ flex: '1 1 calc(50% - 8px)', minWidth: 200 }}
          disabled={isReadOnly}
          helperText="ClickHouse database that will hold website_events for this tenant/site. Auto-created on save. Letters, digits, underscores only."
          inputProps={{ pattern: '^[a-zA-Z][a-zA-Z0-9_]{0,62}$' }}
        />
        <Box sx={{ flex: '1 1 100%' }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={formData.clusterEnabled === true}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  clusterEnabled: e.target.checked,
                  clusterName: e.target.checked ? prev.clusterName : '',
                }))}
                size="small"
                disabled={isReadOnly}
              />
            }
            label="This URL points to a ClickHouse cluster"
          />
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 4, mt: -0.5 }}>
            Enable when the URL is a load balancer in front of several replicas. The table is then
            created as ReplicatedMergeTree with ON CLUSTER DDL, so an event written to whichever
            replica the balancer picked is readable from all of them. Leave off for a single-node
            server — that keeps the local MergeTree behaviour.
          </Typography>
        </Box>
        {formData.clusterEnabled === true && (
          <TextField
            label="Cluster Name"
            placeholder="e.g. pg_cluster"
            value={formData.clusterName || ''}
            onChange={(e) => setFormData(prev => ({ ...prev, clusterName: e.target.value }))}
            required
            size="small"
            sx={{ flex: '1 1 calc(50% - 8px)', minWidth: 200 }}
            disabled={isReadOnly}
            helperText="Cluster name exactly as it appears in system.clusters. Test connection verifies it exists."
            inputProps={{ pattern: '^[a-zA-Z][a-zA-Z0-9_]{0,62}$' }}
          />
        )}
        <Box sx={{ flex: '1 1 100%' }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={formData.preciseVisitorLocation === true}
                onChange={(e) => setFormData(prev => ({ ...prev, preciseVisitorLocation: e.target.checked }))}
                size="small"
                disabled={isReadOnly}
              />
            }
            label="Capture Precise Visitor Location"
          />
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 4, mt: -0.5 }}>
            When enabled, the Infralytiqs SDK on this tenant/site requests the visitor&apos;s precise
            location (browser Geolocation API, subject to the visitor&apos;s consent prompt) and attaches
            latitude/longitude to events. Delivered to the SDK at runtime via the public client-config API.
          </Typography>
        </Box>
        <FormControl size="small" sx={{ flex: '1 1 calc(50% - 8px)', minWidth: 200 }} disabled={isReadOnly}>
          <InputLabel>Default Time Range</InputLabel>
          <Select
            label="Default Time Range"
            value={formData.defaultTimeRange || ''}
            onChange={(e) => setFormData(prev => ({ ...prev, defaultTimeRange: e.target.value }))}
          >
            <MenuItem value="">
              <em>No default (use Last 24 hours)</em>
            </MenuItem>
            {REPORTS_QUICK_RANGES.map((r) => (
              <MenuItem key={r.label} value={r.label}>{r.label}</MenuItem>
            ))}
          </Select>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, ml: 1.5 }}>
            Applied on the Reports page when this server &amp; suite have no saved time-range selection in the browser.
          </Typography>
        </FormControl>
        <FormControl size="small" sx={{ flex: '1 1 calc(50% - 8px)', minWidth: 200 }} disabled={isReadOnly}>
          <InputLabel>Authentication Method</InputLabel>
          <Select
            value={formData.authMethod}
            label="Authentication Method"
            onChange={(e) => setFormData(prev => ({ ...prev, authMethod: e.target.value }))}
          >
            <MenuItem value="none">No Authentication</MenuItem>
            <MenuItem value="basic">Basic Authentication</MenuItem>
          </Select>
        </FormControl>
        {formData.authMethod === 'basic' && (
          <>
            <TextField
              label="Username"
              value={formData.basicAuthUser}
              onChange={(e) => setFormData(prev => ({ ...prev, basicAuthUser: e.target.value }))}
              size="small"
              sx={{ flex: '1 1 calc(50% - 8px)', minWidth: 200 }}
              disabled={isReadOnly}
            />
            <TextField
              label="Password"
              type="password"
              value={formData.basicAuthPassword}
              onChange={(e) => setFormData(prev => ({ ...prev, basicAuthPassword: e.target.value }))}
              size="small"
              sx={{ flex: '1 1 calc(50% - 8px)', minWidth: 200 }}
              disabled={isReadOnly}
            />
          </>
        )}
      </Box>

      <Box sx={{ mt: 2.5 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5, color: 'text.secondary' }}>
          TLS Settings
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', pl: 1 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={formData.tlsSelfSignedCert}
                onChange={(e) => setFormData(prev => ({ ...prev, tlsSelfSignedCert: e.target.checked }))}
                size="small"
                disabled={isReadOnly}
              />
            }
            label="Add Self-signed certificate"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={formData.tlsClientAuth}
                onChange={(e) => setFormData(prev => ({ ...prev, tlsClientAuth: e.target.checked }))}
                size="small"
                disabled={isReadOnly}
              />
            }
            label="TLS client Authentication"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={formData.tlsSkipValidation}
                onChange={(e) => setFormData(prev => ({ ...prev, tlsSkipValidation: e.target.checked }))}
                size="small"
                disabled={isReadOnly}
              />
            }
            label="Skip TLS Certificate validation"
          />
        </Box>
      </Box>

      <Box sx={{ mt: 2.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
            HTTP Headers
          </Typography>
          {!isReadOnly && (
            <Button size="small" variant="outlined" color="secondary" startIcon={<Add />} onClick={handleAddHeader}>
              Add Header
            </Button>
          )}
        </Box>
        {formData.httpHeaders.map((header, index) => (
          <Box key={index} sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center' }}>
            <TextField
              size="small"
              label="Header Key"
              value={header.key}
              onChange={(e) => handleHeaderChange(index, 'key', e.target.value)}
              sx={{ flex: 1 }}
              disabled={isReadOnly}
            />
            <TextField
              size="small"
              label="Header Value"
              value={header.value}
              onChange={(e) => handleHeaderChange(index, 'value', e.target.value)}
              sx={{ flex: 1 }}
              disabled={isReadOnly}
            />
            {!isReadOnly && (
              <IconButton size="small" color="error" onClick={() => handleRemoveHeader(index)}>
                <RemoveCircleOutlineIcon fontSize="small" />
              </IconButton>
            )}
          </Box>
        ))}
        {formData.httpHeaders.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ pl: 1 }}>
            No custom headers configured.
          </Typography>
        )}
      </Box>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 2.5 }}>
        <TextField
          label="Request Timeout (seconds)"
          type="number"
          value={formData.requestTimeout}
          onChange={(e) => setFormData(prev => ({ ...prev, requestTimeout: parseInt(e.target.value) || 30 }))}
          size="small"
          sx={{ flex: '1 1 calc(50% - 8px)', minWidth: 200 }}
          inputProps={{ min: 1, max: 300 }}
          disabled={isReadOnly}
        />
        <TextField
          label="Allowed Cookie Names"
          placeholder="cookie1, cookie2"
          value={formData.allowedCookies}
          onChange={(e) => setFormData(prev => ({ ...prev, allowedCookies: e.target.value }))}
          size="small"
          sx={{ flex: '1 1 calc(50% - 8px)', minWidth: 200 }}
          disabled={isReadOnly}
        />
      </Box>

      {showCompanyUserAssignPicker && (
        <Box sx={{ mt: 2.5 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5, color: 'text.secondary' }}>
            Assigned users
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
            Search below to add users. Selected users appear as tiles under the field so large lists stay readable.
          </Typography>
          <Autocomplete
            multiple
            disableCloseOnSelect
            disabled={isReadOnly}
            options={companyMembers}
            getOptionLabel={(option) => option.label}
            isOptionEqualToValue={(a, b) => a._id === b._id}
            value={assignedUserAutocompleteValue}
            onChange={(_, newValue) => {
              setFormData((prev) => ({
                ...prev,
                assignedUserIds: newValue.map((m) => m._id),
              }));
            }}
            renderTags={() => null}
            renderInput={(params) => (
              <TextField {...params} size="small" label="Add users" placeholder="Search and select" />
            )}
          />
          {(assignedUserAutocompleteValue.length > 0 || !isReadOnly) && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                Selected users ({assignedUserAutocompleteValue.length})
              </Typography>
              {assignedUserAutocompleteValue.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No users selected yet.
                </Typography>
              ) : (
                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 1,
                    maxHeight: 280,
                    overflowY: 'auto',
                    pr: 0.5,
                  }}
                >
                  {assignedUserAutocompleteValue.map((member) => (
                    <Paper
                      key={member._id}
                      variant="outlined"
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 0.75,
                        py: 0.75,
                        px: 1.25,
                        borderRadius: 2,
                        maxWidth: '100%',
                        borderWidth: 1,
                        borderStyle: 'solid',
                        borderColor: 'secondary.main',
                      }}
                    >
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography variant="body2" fontWeight={600} noWrap title={member.label}>
                          {member.label}
                        </Typography>
                        {member.email ? (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            noWrap
                            display="block"
                            title={member.email}
                          >
                            {member.email}
                          </Typography>
                        ) : null}
                      </Box>
                      {!isReadOnly && (
                        <Button
                          variant="contained"
                          color="error"
                          size="small"
                          aria-label={`Remove ${member.label}`}
                          onClick={() => {
                            setFormData((prev) => ({
                              ...prev,
                              assignedUserIds: (prev.assignedUserIds || []).filter((id) => id !== member._id),
                            }));
                          }}
                          sx={{
                            flexShrink: 0,
                            ml: 0.25,
                            minWidth: 32,
                            width: 32,
                            height: 32,
                            p: 0,
                            borderRadius: 1,
                            bgcolor: 'error.main',
                            color: 'error.contrastText',
                            boxShadow: 'none',
                            '&:hover': {
                              bgcolor: 'error.dark',
                              boxShadow: 'none',
                            },
                          }}
                        >
                          <CloseIcon sx={{ fontSize: 18, color: 'inherit' }} />
                        </Button>
                      )}
                    </Paper>
                  ))}
                </Box>
              )}
            </Box>
          )}
        </Box>
      )}

      {!isReadOnly && (
        <Box sx={{ mt: 2.5, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            color="secondary"
            startIcon={
              lokiTestLoading ? (
                <CircularProgress size={18} color="inherit" />
              ) : (
                <NetworkCheck />
              )
            }
            onClick={handleTestClickHouseConnection}
            disabled={lokiTestLoading || !formData.url.trim()}
            sx={{
              borderColor: 'secondary.main',
              color: 'secondary.main',
              '&:hover': { borderColor: 'secondary.dark', color: 'secondary.dark' },
              '&.Mui-disabled': (t) => (t.palette.mode === 'light'
                ? {
                  borderColor: 'rgba(25, 25, 25, 0.38)',
                  color: 'rgb(25, 25, 25)',
                  backgroundColor: 'rgba(25, 25, 25, 0.04)',
                }
                : {}),
            }}
          >
            Test connection
          </Button>
          <Typography variant="caption" color="text.secondary">
            Run a test before saving. Changing URL, auth, TLS, headers, or timeout requires a new test.
          </Typography>
        </Box>
      )}

      {lokiTestSeverity && (
        <Alert severity={lokiTestSeverity === 'success' ? 'success' : lokiTestSeverity === 'error' ? 'error' : 'info'} sx={{ mt: 2 }}>
          {lokiTestMessage}
        </Alert>
      )}
    </Box>
  );

  const renderComingSoon = () => (
    <Box sx={{ textAlign: 'center', py: 5 }}>
      <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
        Coming Soon
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {MODULE_TYPES.find(m => m.key === selectedModule)?.label} configuration will be available soon.
      </Typography>
    </Box>
  );

  if (!canAccessInfralytiqsSettings) {
    return (
      <Box sx={{ p: { xs: '10px', md: '20px' } }}>
        <DynamicBreadcrumbs items={[
          { itemPath: '/', itemLabel: 'Home' },
          { itemPath: '/infralytiqs/settings', itemLabel: 'Settings' },
        ]} />
        <Alert severity="warning" sx={{ mt: 2 }}>
          You do not have permission to access Infralytiqs settings. This page is available to company administrators with an active infralytiqs.com license.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: '10px', md: '20px' } }}>
      <DynamicBreadcrumbs items={[
        { itemPath: '/', itemLabel: 'Home' },
        { itemPath: '/infralytiqs/settings', itemLabel: 'Settings' },
      ]} />

      <TenantsAccordion />

      <Accordion
        sx={{
          mb: 2,
          borderRadius: '12px !important',
          '&:before': { display: 'none' },
          overflow: 'hidden',
        }}
      >
        <AccordionSummary
          expandIcon={(
            <ExpandMore sx={{
              color: theme.palette.dashboard?.welcomePanelRightTextColor
                ?? (theme.palette.mode === 'dark' ? 'black' : 'inherit'),
            }}
            />
          )}
          sx={{
            px: 3,
            py: 1,
            borderRadius: '12px',
            background: theme.palette.dashboard?.welcomePanelGradient
              ?? `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
            '& .MuiAccordionSummary-content': { my: 1 },
          }}
        >
          <Box display="flex" alignItems="center" gap={1}>
            <Storage sx={{
              color: theme.palette.dashboard?.welcomePanelLeftTextColor
                ?? (theme.palette.mode === 'light' ? 'black' : 'inherit'),
            }}
            />
            <Typography
              variant="h6"
              sx={{
                fontWeight: 'bold',
                color: theme.palette.dashboard?.welcomePanelLeftTextColor
                  ?? (theme.palette.mode === 'light' ? 'black' : 'inherit'),
              }}
            >
              Data Sources
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ px: { xs: 1.5, sm: 2 }, py: 2 }}>
          {/* Toolbar */}
          <Paper
            elevation={0}
            sx={{
              p: 1.5,
              mb: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: 1,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
            }}
          >
            <TextField
              size="small"
              placeholder="Search configurations..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
              sx={{ minWidth: 250, flex: { xs: '1 1 100%', sm: '0 1 auto' } }}
            />

            <Box sx={{ display: 'flex', alignItems: 'center', ml: { xs: 0, sm: 'auto' }, width: { xs: '100%', sm: 'auto' }, justifyContent: { xs: 'flex-end', sm: 'flex-end' } }}>
              <Button
                variant="contained"
                color="secondary"
                startIcon={<Add />}
                endIcon={<ArrowDropDown />}
                onClick={handleNewConfigMenuOpen}
                sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
              >
                New
              </Button>
              <Menu
                anchorEl={newConfigMenuAnchor}
                open={Boolean(newConfigMenuAnchor)}
                onClose={handleNewConfigMenuClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                slotProps={{ paper: { sx: { minWidth: 220, mt: 0.5 } } }}
              >
                {addableModuleTypes.map((moduleType) => (
                  <MenuItem
                    key={moduleType.key}
                    onClick={() => handleNewConfigTypeSelect(moduleType.key)}
                  >
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <ModuleLogo src={moduleType.logo} alt={moduleType.label} size={24} />
                    </ListItemIcon>
                    <ListItemText primary={moduleType.label} />
                  </MenuItem>
                ))}
              </Menu>
            </Box>
          </Paper>

          {/* Content Panels */}
          {(() => {
            const hasConfigured = filteredConfigurations.length > 0;

            if (!hasConfigured) {
              return (
                <Paper sx={{ p: 4, textAlign: 'center' }}>
                  <Typography variant="body1" color="text.secondary">
                    {searchQuery
                      ? 'No results match your search.'
                      : 'No server configurations yet. Use the New button to add a Loki or ClickHouse server.'}
                  </Typography>
                </Paper>
              );
            }

            return (
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  borderRadius: 2,
                  bgcolor: (t) => (t.palette.mode === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)'),
                }}
              >
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5, color: 'text.secondary' }}>
                  Configured Servers
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                  ClickHouse servers marked with a green tick are the default for the Reports page when a user has not visited Reports before.
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2.5 }}>
                  {filteredConfigurations.map(renderConfiguredTile)}
                </Box>
              </Paper>
            );
          })()}
        </AccordionDetails>
      </Accordion>

      {/* Add Module Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={handleDialogClose}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
          <Typography variant="h6" component="span" fontWeight={600}>
            {dialogMode === 'view' ? 'View' : dialogMode === 'edit' ? 'Edit' : 'Configure'} {MODULE_TYPES.find(m => m.key === selectedModule)?.label}
          </Typography>
          <IconButton onClick={handleDialogClose} size="small" edge="end">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 2 }}>
          {selectedModule === 'Loki' && renderLokiForm()}
          {selectedModule === 'ClickHouse' && renderClickHouseForm()}
          {selectedModule && selectedModule !== 'Loki' && selectedModule !== 'ClickHouse' && renderComingSoon()}
        </DialogContent>
        {(selectedModule === 'Loki' || selectedModule === 'ClickHouse') && dialogMode !== 'view' && (
          <DialogActions>
            <Button
              variant="outlined"
              color="secondary"
              onClick={handleDialogClose}
              startIcon={<Cancel />}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              startIcon={<Save />}
              variant="contained"
              color="success"
              disabled={
                !formData.name.trim() ||
                !formData.url.trim() ||
                !lokiConnectionVerified ||
                (
                  selectedModule === 'ClickHouse' && (
                    !formData.tenantId?.trim()
                    || !formData.dbName?.trim()
                    || !/^[a-zA-Z][a-zA-Z0-9_]{0,62}$/.test(formData.dbName?.trim() || '')
                    || (
                      formData.clusterEnabled === true
                      && !/^[a-zA-Z][a-zA-Z0-9_]{0,62}$/.test(formData.clusterName?.trim() || '')
                    )
                  )
                )
              }
            >
              {dialogMode === 'edit' ? 'Update' : 'Submit'}
            </Button>
          </DialogActions>
        )}
        {(selectedModule === 'Loki' || selectedModule === 'ClickHouse') && dialogMode === 'view' && (
          <DialogActions>
            <Button
              variant="outlined"
              color="secondary"
              onClick={handleDialogClose}
              startIcon={<Cancel />}
            >
              Close
            </Button>
          </DialogActions>
        )}
      </Dialog>

      {/* Loading Overlay */}
      <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loading}>
        <CircularProgress color="inherit" />
      </Backdrop>

      {/* Notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};
